import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * SIP Trunk 外撥顯示號碼 / 撥號前置碼（活動層）
 *
 * 分機模式靠「選分機」決定顯號；Trunk 一條線多通，改成每通帶 CallerID，這裡讓用戶挑要顯示哪個號碼：
 *   - 預設（tenant.trunk.callerId，或由 PBX 決定）
 *   - 這個租戶的進線 DID 之一（下拉，省打字）
 *   - 自訂輸入
 * 前置碼給「PBX 不放行自帶顯號、改用前置碼挑外線」的客戶用。
 *
 * props: trunkInfo {callerIdMode, callerId, dids[]} / callerId / onCallerId / dialPrefix / onDialPrefix / dense
 */
export function TrunkCallerIdFields({ trunkInfo, callerId, onCallerId, dialPrefix, onDialPrefix, dense = false }) {
    const { t } = useTranslation('outbound');
    const dids = Array.isArray(trunkInfo?.dids) ? trunkInfo.dids.map(String) : [];
    const [mode, setMode] = useState(callerId ? (dids.includes(callerId) ? callerId : 'custom') : '');
    // 外部值變了（載入 / 重設）才同步；使用者剛選「自訂」還沒打字時 callerId='' 不要把 mode 彈回預設
    useEffect(() => { if (callerId) setMode(dids.includes(callerId) ? callerId : 'custom'); /* eslint-disable-next-line */ }, [callerId]);

    const isCustomMode = trunkInfo?.callerIdMode === 'custom';
    const defaultLabel = isCustomMode && trunkInfo?.callerId
        ? t('trunkCid.defaultVal', { n: trunkInfo.callerId })
        : t('trunkCid.defaultPbx');
    const inputCls = 'px-2 py-1.5 text-sm border border-cream-300 rounded bg-white focus:outline-none focus:border-accent-500';

    return (
        <>
            <Row dense={dense} label={t('trunkCid.label')}>
                <div className="flex items-center gap-2 flex-wrap">
                    <select value={mode} className={inputCls}
                        onChange={(e) => { const v = e.target.value; setMode(v); onCallerId(v === 'custom' ? '' : v); }}>
                        <option value="">{defaultLabel}</option>
                        {dids.map(d => <option key={d} value={d}>{d}</option>)}
                        <option value="custom">{t('trunkCid.custom')}</option>
                    </select>
                    {mode === 'custom' && (
                        <input value={callerId || ''} placeholder={t('trunkCid.customPh')} className={`${inputCls} w-44 tabular-nums`}
                            onChange={(e) => onCallerId(e.target.value.replace(/[^0-9+*#]/g, '').slice(0, 32))} />
                    )}
                </div>
                <div className={`text-[11px] mt-1 ${isCustomMode ? 'text-ink-400' : 'text-warn-500'}`}>
                    {isCustomMode ? t('trunkCid.hintCustom') : t('trunkCid.hintPbx')}
                </div>
            </Row>
            <Row dense={dense} label={t('trunkCid.prefixLabel')}>
                <input value={dialPrefix || ''} placeholder={t('trunkCid.prefixPh')} className={`${inputCls} w-32 tabular-nums`}
                    onChange={(e) => onDialPrefix(e.target.value.replace(/[^0-9*#]/g, '').slice(0, 32))} />
                <div className="text-[11px] text-ink-400 mt-1">{t('trunkCid.prefixHint')}</div>
            </Row>
        </>
    );
}

// ⚠️ 一定要定義在元件外：放在 render 內會每次重繪換一個新元件型別 → React 整段 unmount/remount，
//    輸入框打一個字就掉焦點（staging 實測 select / input 「無法互動」就是這個）。
function Row({ dense, label, children }) {
    return dense
        ? <div className="flex items-start gap-3 py-2 border-b border-cream-100"><div className="text-xs text-ink-400 w-24 shrink-0 pt-1.5">{label}</div><div className="flex-1">{children}</div></div>
        : <div><div className="text-xs text-ink-700 mb-1.5">{label}</div>{children}</div>;
}

/** 顯示用：活動實際會帶的外顯號碼描述 */
export function describeTrunkCallerId(t, trunkInfo, callerId) {
    if (callerId) return callerId;
    if (trunkInfo?.callerIdMode === 'custom' && trunkInfo?.callerId) return t('trunkCid.defaultVal', { n: trunkInfo.callerId });
    return t('trunkCid.defaultPbx');
}
